import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { assetsApi } from '@/api/assets'
import { isVideoMediaUrl, resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'
import { notifyApiError, toast } from '@/shared/lib/notify'
import { cn } from '@/shared/lib/utils'

const ACCEPT = 'image/jpeg,image/png,image/webp,image/svg+xml,.svg'

type CoverImageUploadProps = {
  value: string
  onChange: (url: string) => void
  /** 自定义上传；未传则走 assetsApi.upload */
  onUpload?: (file: File) => Promise<string>
  /** 展示用 URL 解析；默认 shared media-proxy */
  resolveDisplayUrl?: (url: string) => string
  disabled?: boolean
  className?: string
  /** 方框边长，默认 88px（与管理端一致） */
  size?: number
  hint?: string
  successMessage?: string
}

export function CoverImageUpload({
  value,
  onChange,
  onUpload,
  resolveDisplayUrl = resolveMediaDisplayUrl,
  disabled = false,
  className,
  size = 88,
  hint,
  successMessage,
}: CoverImageUploadProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [localPreview, setLocalPreview] = useState('')
  const [loadFailed, setLoadFailed] = useState(false)

  const displaySrc = localPreview || resolveDisplayUrl(value)
  const coverIsVideo = !localPreview && isVideoMediaUrl(value)

  function revokeLocalPreview() {
    if (localPreview) {
      URL.revokeObjectURL(localPreview)
      setLocalPreview('')
    }
  }

  useEffect(() => {
    setLoadFailed(false)
  }, [value])

  useEffect(() => () => revokeLocalPreview(), [])

  async function handleFile(file: File) {
    setUploading(true)
    setLoadFailed(false)
    revokeLocalPreview()
    const blobUrl = URL.createObjectURL(file)
    setLocalPreview(blobUrl)
    try {
      let url: string
      if (onUpload) {
        url = (await onUpload(file)).trim()
      } else {
        const asset = await assetsApi.upload(file, 'image')
        url = asset.url?.trim() ?? ''
      }
      if (!url) throw new Error(t('account.publicProfile.coverUploadFailed'))
      onChange(url)
      toast.success(successMessage ?? t('common.coverImageUpload.uploaded'))
    } catch (err) {
      revokeLocalPreview()
      notifyApiError(err, { fallback: t('account.publicProfile.coverUploadFailed') })
    } finally {
      setUploading(false)
    }
  }

  function clear() {
    revokeLocalPreview()
    setLoadFailed(false)
    onChange('')
  }

  function onMediaLoad(ev: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>) {
    setLoadFailed(false)
    const el = ev.currentTarget
    const src =
      el instanceof HTMLVideoElement
        ? el.currentSrc || el.src
        : el.currentSrc || el.src
    if (localPreview && src.startsWith('blob:')) {
      revokeLocalPreview()
    }
  }

  function onImgError() {
    if (localPreview || value) setLoadFailed(true)
  }

  const boxStyle = { width: size, height: size }

  return (
    <div className={cn('inline-flex flex-col', className)}>
      <div className="relative inline-block">
        <button
          type="button"
          disabled={disabled || uploading}
          style={boxStyle}
          className={cn(
            'relative grid place-items-center overflow-hidden rounded-lg border border-dashed transition-colors',
            'border-border bg-muted/40 hover:border-primary/60 hover:bg-muted/60',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            (disabled || uploading) && 'cursor-not-allowed opacity-60',
            loadFailed && 'border-amber-500/60',
          )}
          onClick={() => inputRef.current?.click()}
          aria-label={t('script.coverUpload')}
        >
          {displaySrc && !uploading ? (
            <>
              {coverIsVideo ? (
                <video
                  src={displaySrc}
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover"
                  onLoadedData={onMediaLoad}
                  onError={onImgError}
                />
              ) : (
                <img
                  src={displaySrc}
                  alt=""
                  className="h-full w-full object-cover"
                  onLoad={onMediaLoad}
                  onError={onImgError}
                />
              )}
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-black/45 text-[11px] text-white opacity-0 transition-opacity hover:opacity-100">
                <span className="text-[22px] font-light leading-none">+</span>
                <span>{t('common.coverImageUpload.replace')}</span>
              </span>
            </>
          ) : uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
          ) : (
            <span className="text-[32px] font-light leading-none text-muted-foreground">+</span>
          )}
        </button>

        {(value || localPreview) && !uploading ? (
          <button
            type="button"
            disabled={disabled}
            title={t('common.coverImageUpload.remove')}
            aria-label={t('common.coverImageUpload.remove')}
            className={cn(
              'absolute -right-1.5 -top-1.5 z-[1] grid h-[22px] w-[22px] place-items-center',
              'rounded-full border border-border bg-popover text-sm text-muted-foreground shadow-sm',
              'transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            onClick={(e) => {
              e.stopPropagation()
              clear()
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        disabled={disabled || uploading}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleFile(file)
          e.target.value = ''
        }}
      />

      {loadFailed ? (
        <p className="mt-2 text-xs leading-relaxed text-amber-500/90">
          {t('common.coverImageUpload.loadError')}
        </p>
      ) : null}
      {hint ? <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
    </div>
  )
}
